import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { TARGETS, CONCEPTS } from "../utils/buildPrompt";

const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <mask id="bk" style={{maskType:'alpha'}} maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
      <rect width="20" height="20" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#bk)">
      <path d="M11.625 15.7917L5.83333 10L11.625 4.20833L12.8125 5.39583L8.20833 10L12.8125 14.6042L11.625 15.7917Z" fill="#111111"/>
    </g>
  </svg>
);

const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4.66667 12.0002V1.3335H13.3333V12.0002H4.66667ZM2 14.6668V4.00016H3.33333V13.3335H10.6667V14.6668H2Z" fill="#111111"/>
  </svg>
);

const IconShare = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M11.3335 14.6666C10.7779 14.6666 10.3057 14.4721 9.91683 14.0833C9.52794 13.6944 9.3335 13.2221 9.3335 12.6666C9.3335 12.5999 9.35016 12.4444 9.3835 12.1999L4.70016 9.46658C4.52238 9.63325 4.31683 9.76392 4.0835 9.85858C3.85016 9.95325 3.60016 10.0004 3.3335 9.99992C2.77794 9.99992 2.30572 9.80547 1.91683 9.41658C1.52794 9.0277 1.3335 8.55547 1.3335 7.99992C1.3335 7.44436 1.52794 6.97214 1.91683 6.58325C2.30572 6.19436 2.77794 5.99992 3.3335 5.99992C3.60016 5.99992 3.85016 6.04725 4.0835 6.14192C4.31683 6.23659 4.52238 6.36703 4.70016 6.53325L9.3835 3.79992C9.36127 3.72214 9.3475 3.64725 9.34216 3.57525C9.33683 3.50325 9.33394 3.42259 9.3335 3.33325C9.3335 2.7777 9.52794 2.30547 9.91683 1.91659C10.3057 1.5277 10.7779 1.33325 11.3335 1.33325C11.8891 1.33325 12.3613 1.5277 12.7502 1.91659C13.1391 2.30547 13.3335 2.7777 13.3335 3.33325C13.3335 3.88881 13.1391 4.36103 12.7502 4.74992C12.3613 5.13881 11.8891 5.33325 11.3335 5.33325C11.0668 5.33325 10.8168 5.28592 10.5835 5.19125C10.3502 5.09659 10.1446 4.96614 9.96683 4.79992L5.2835 7.53325C5.30572 7.61103 5.31972 7.68614 5.3255 7.75858C5.33127 7.83103 5.33394 7.91147 5.3335 7.99992C5.33305 8.08836 5.33038 8.16903 5.3255 8.24192C5.32061 8.31481 5.30661 8.3897 5.2835 8.46658L9.96683 11.1999C10.1446 11.0333 10.3502 10.9028 10.5835 10.8086C10.8168 10.7144 11.0668 10.667 11.3335 10.6666C11.8891 10.6666 12.3613 10.861 12.7502 11.2499C13.1391 11.6388 13.3335 12.111 13.3335 12.6666C13.3335 13.2221 13.1391 13.6944 12.7502 14.0833C12.3613 14.4721 11.8891 14.6666 11.3335 14.6666Z" fill="#111111"/>
  </svg>
);

function getModeLabel(item) {
  if (item.tab === 'target' && item.target) {
    const label = TARGETS[item.target]?.label ?? item.target;
    const level = TARGETS[item.target]?.levels?.[item.level_idx] ?? '';
    const detail = item.detail ? ` · ${item.detail}` : '';
    return `${label} ${level}${detail}`.trim();
  }
  if (item.tab === 'concept' && item.concept) {
    return CONCEPTS[item.concept]?.label ?? item.concept;
  }
  return '-';
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const min = Math.floor(diff / 60000);
  const hour = Math.floor(diff / 3600000);
  const day = Math.floor(diff / 86400000);
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  if (hour < 24) return `${hour}시간 전`;
  if (day < 7) return `${day}일 전`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export default function ArchivePage({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('history');
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    const load = async () => {
      setLoading(true);
      const [{ data: h }, { data: f }] = await Promise.all([
        supabase.from('conversion_history').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('favorites').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      ]);
      setHistory(h ?? []);
      setFavorites(f ?? []);
      setLoading(false);
    };
    load();
  }, [user, navigate]);

  const handleCopy = async (text, id) => {
    try { await navigator.clipboard.writeText(text); } catch {}
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  const handleShare = async (item) => {
    // 보관함 항목은 shares 테이블에 없으므로, 공유 시점에 공유용 레코드를 생성
    const { data: shareData } = await supabase.from('shares').insert({
      user_id: user?.id ?? null,
      tab: item.tab,
      target: item.target,
      level_idx: item.level_idx,
      concept: item.concept,
      detail: item.detail,
      input_text: item.input_text,
      result_text: item.result_text,
    }).select().single();
    if (!shareData) return;

    const shareUrl = `https://toneplz.vercel.app/share/${shareData.id}`;
    try {
      if (navigator.share) {
        // url만 전달 — text/title에 링크를 함께 넣으면 카카오톡에서 중복 전송됨
        await navigator.share({ url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
    } catch {
      // 공유 시트 취소 등 — 무시
    }
  };

  const handleDeleteHistory = async (id) => {
    await supabase.from('conversion_history').delete().eq('id', id);
    setHistory(prev => prev.filter(h => h.id !== id));
  };

  const handleDeleteFavorite = async (id) => {
    await supabase.from('favorites').delete().eq('id', id);
    setFavorites(prev => prev.filter(f => f.id !== id));
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0f0', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 430, background: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'DM Mono', monospace" }}>

        <div style={{ background: '#ffffff', borderBottom: '1px solid #111111', padding: '0 16px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <button onClick={() => navigate('/my')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
            <IconBack />
          </button>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111111', letterSpacing: 1 }}>보관함</span>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, background: '#f5f5f5', borderRadius: 12, padding: 3 }}>
            {[['history', '변환기록'], ['favorites', '즐겨찾기']].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ flex: 1, padding: '7px 0', background: tab === t ? '#111111' : 'transparent', color: tab === t ? '#ffffff' : '#888888', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: tab === t ? 500 : 400, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbbbbb', fontSize: 15 }}>
              불러오는 중...
            </div>
          ) : tab === 'history' ? (
            history.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#bbbbbb' }}>
                <span style={{ fontSize: 40 }}>💬</span>
                <span style={{ fontSize: 15 }}>변환 기록이 없어요</span>
              </div>
            ) : (
              history.map(item => (
                <div key={item.id} style={{ background: '#ffffff', border: '1px solid #111111', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ background: '#f5f5f5', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eeeeee' }}>
                    <span style={{ fontSize: 13, color: '#555555', fontWeight: 600 }}>{getModeLabel(item)}</span>
                    <span style={{ fontSize: 13, color: '#bbbbbb' }}>{formatDate(item.created_at)}</span>
                  </div>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid #eeeeee' }}>
                    <div style={{ fontSize: 13, color: '#bbbbbb', marginBottom: 4, letterSpacing: 0.5 }}>하고싶었던 말</div>
                    <div style={{ fontSize: 15, color: '#777', lineHeight: 1.6, wordBreak: 'keep-all' }}>{item.input_text}</div>
                  </div>
                  <div style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, color: '#bbbbbb', marginBottom: 4, letterSpacing: 0.5 }}>변환된 말</div>
                    <div style={{ fontSize: 15, color: '#111111', lineHeight: 1.7, wordBreak: 'keep-all', whiteSpace: 'pre-wrap' }}>{item.result_text}</div>
                  </div>
                  <div style={{ padding: '8px 14px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => handleDeleteHistory(item.id)}
                      style={{ background: 'none', border: 'none', fontSize: 13, color: '#111111', cursor: 'pointer', padding: 0, fontFamily: "'DM Mono', monospace" }}>
                      삭제
                    </button>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleShare(item)}
                        style={{ background: 'none', border: '1px solid #111111', borderRadius: 8, padding: '5px 12px', fontSize: 13, color: '#111111', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'DM Mono', monospace" }}>
                        <IconShare /> 공유
                      </button>
                      <button onClick={() => handleCopy(item.result_text, item.id)}
                        style={{ background: 'none', border: '1px solid #111111', borderRadius: 8, padding: '5px 12px', fontSize: 13, color: '#111111', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'DM Mono', monospace" }}>
                        <IconCopy /> {copiedId === item.id ? '복사됨' : '복사'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            favorites.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#bbbbbb' }}>
                <span style={{ fontSize: 40 }}>★</span>
                <span style={{ fontSize: 15 }}>즐겨찾기가 없어요</span>
              </div>
            ) : (
              favorites.map(item => (
                <div key={item.id} style={{ background: '#ffffff', border: '1px solid #111111', borderRadius: 12, overflow: 'hidden', padding: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 22, color: '#FFD700', flexShrink: 0 }}>★</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#111111' }}>
                        {item.tab === 'target' ? (TARGETS[item.target]?.label ?? item.target) : (CONCEPTS[item.concept]?.label ?? item.concept)}
                      </div>
                      <div style={{ fontSize: 13, color: '#888888', marginTop: 2, display: 'flex', gap: 6 }}>
                        {item.tab === 'target' && TARGETS[item.target]?.levels?.[item.level_idx] && (
                          <span>{TARGETS[item.target].levels[item.level_idx]}</span>
                        )}
                        {item.detail && (
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {item.detail}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteFavorite(item.id)}
                    style={{ background: 'none', border: 'none', fontSize: 13, color: '#bbbbbb', cursor: 'pointer', padding: 0, flexShrink: 0, fontFamily: "'DM Mono', monospace" }}>
                    삭제
                  </button>
                </div>
              ))
            )
          )}
        </div>

      </div>
    </div>
  );
}